import { combineReducers } from 'redux';
import {
  FETCH_TEAM_MEMBERS_SUCCESS,
  FetchTeamMembersSuccessAction,
  NEW_INVITES_SENT_SUCCESS,
  NewInvitesSentSuccessAction,
  REMOVE_CONTRIBUTOR_SUCCESS,
  RemoveContributorSuccessAction,
  RESEND_INVITE_SUCCESS,
  ResendInviteSuccessAction,
  REVOKE_INVITE_SUCCESS,
  RevokeInviteSuccessAction,
  SET_INVITE_ROLE_SUCCESS,
  SET_USER_ROLE_SUCCESS,
  SetInviteRoleSuccessAction,
  SetUserRoleSuccessAction,
  SET_TEAM_ROLE_SUCCESS,
  SetTeamRoleSuccessAction,
  CancelTransferOwnershipSuccessAction,
  CANCEL_TRANSFER_OWNERSHIP_SUCCESS,
  SetTeamMemberRolesFailedAction,
  SET_TEAM_MEMBER_ROLES_FAILED,
  RevokeInviteFailedAction,
  REVOKE_INVITE_FAILED,
  RemoveContributorFailedAction,
  REMOVE_CONTRIBUTOR_FAILED,
} from '../actions/action-types';
import { InviteData, UserData, TeamData } from '../../types/teamMembers';

export type TeamMembersAction =
  FetchTeamMembersSuccessAction
  | NewInvitesSentSuccessAction
  | RemoveContributorSuccessAction
  | RevokeInviteSuccessAction
  | SetInviteRoleSuccessAction
  | SetUserRoleSuccessAction
  | ResendInviteSuccessAction
  | SetTeamRoleSuccessAction
  | CancelTransferOwnershipSuccessAction
  | SetTeamMemberRolesFailedAction
  | RemoveContributorFailedAction
  | RevokeInviteFailedAction;

const sliceItemAtIndex = (array: any[], index: number) => {
  return [
    ...array.slice(0, index),
    ...array.slice(index + 1, array.length)
  ];
};

const updateItemAtIndex = (array: any[], index: number, updatedItem) => {
  return [
    ...array.slice(0, index),
    updatedItem,
    ...array.slice(index + 1, array.length)
  ];
};

const updateInvite = (state, updatedInvite: InviteData) => {
  const index = findIndexById(state, updatedInvite.id);
  return updateItemAtIndex(state, index, updatedInvite);
};

const updateRoleById = (state, itemId: string, newRoles: string[]) =>
  state.map((item) => itemId === item.id ? { ...item, roles: newRoles } : item);

const removePendingOwnership = (state) =>
  state.filter(item => !(item.isPendingOwner && !item.id))
    .map(item => {
    delete item.isPendingOwner;
    return item;
  });

export const users = (state: UserData[] = [], action: TeamMembersAction): UserData[] => {
  switch (action.type) {
    case FETCH_TEAM_MEMBERS_SUCCESS:
      return action.roles.users;
    case REMOVE_CONTRIBUTOR_SUCCESS:
      const removedIndex = findIndexById(state, action.removedContributorId);
      return sliceItemAtIndex(state, removedIndex);
    case REMOVE_CONTRIBUTOR_FAILED:
      if (action.user) {
        return state.concat([action.user]);
      } else {
        return state;
      }
    case SET_USER_ROLE_SUCCESS:
      return updateRoleById(state, action.userId, action.roles);
    case SET_TEAM_MEMBER_ROLES_FAILED:
      return updateRoleById(state, action.teamMemberId, action.roles);
    case CANCEL_TRANSFER_OWNERSHIP_SUCCESS:
      return removePendingOwnership(state);
    default:
      return state;
  }
};

export const invites = (state: InviteData[] = [], action: TeamMembersAction): InviteData[] => {
  switch (action.type) {
    case FETCH_TEAM_MEMBERS_SUCCESS:
      return action.roles.invites;
    case NEW_INVITES_SENT_SUCCESS:
      return [...state, ...action.newInvites];
    case REVOKE_INVITE_SUCCESS:
      const removedIndex = findIndexById(state, action.revokedInviteId);
      return sliceItemAtIndex(state, removedIndex);
    case REVOKE_INVITE_FAILED:
      return state.concat([action.invite]);
    case RESEND_INVITE_SUCCESS:
      return updateInvite(state, action.updatedInviteData);
    case SET_INVITE_ROLE_SUCCESS:
      return updateRoleById(state, action.inviteId, action.roles);
    case SET_TEAM_MEMBER_ROLES_FAILED:
      return updateRoleById(state, action.teamMemberId, action.roles);
    case CANCEL_TRANSFER_OWNERSHIP_SUCCESS:
      return removePendingOwnership(state);
    default:
      return state;
  }
};

export const teams = (state: TeamData[] = [], action: TeamMembersAction): TeamData[] => {
  switch (action.type) {
    case FETCH_TEAM_MEMBERS_SUCCESS:
      return action.roles.teams ? action.roles.teams : [];
    case REMOVE_CONTRIBUTOR_SUCCESS:
      const removedIndex = findIndexById(state, action.removedContributorId);
      return sliceItemAtIndex(state, removedIndex);
    case REMOVE_CONTRIBUTOR_FAILED:
      if (action.team) {
        return state.concat([action.team]);
      } else {
        return state;
      }
    case SET_TEAM_ROLE_SUCCESS:
      return updateRoleById(state, action.accountId, action.roles);
    case SET_TEAM_MEMBER_ROLES_FAILED:
      return updateRoleById(state, action.teamMemberId, action.roles);
    case CANCEL_TRANSFER_OWNERSHIP_SUCCESS:
      return removePendingOwnership(state);
    default:
      return state;
  }
};

const findIndexById = (arr, id) => arr.findIndex(data => data.id === id);

export const teamMembers = combineReducers({
  users,
  invites,
  teams
});
